import * as stylex from '@stylexjs/stylex'
import { memo, useMemo, type ComponentPropsWithoutRef, type ElementType, type ForwardedRef, type ReactNode } from 'react'
import isEqual from 'react-fast-compare'
import type { TableCellPadding, TableCellSize } from '../TableCell/TableCell'
import { colors } from '../tokens/colors.stylex'
import { typography } from '../tokens/typography.stylex'
import { makeExtendStyle, type ExtendStyleProps } from '../utils/makeExtendStyle/makeExtendStyle'
import { TableContext, type TableContextValue } from './TableContext'

const _baseStyles = stylex.create({
  root: {
    borderCollapse: 'collapse',
    borderSpacing: 0,
    display: 'table',
    width: '100%', // Follow MUI approach - simple 100% width
  },
  stickyHeader: {
    borderCollapse: 'separate',
  },
  caption: {
    padding: 16,
    captionSide: 'bottom',
    color: colors.grey600,
    fontSize: typography.sizeBody,
    lineHeight: typography.lineHeightBody,
    textAlign: 'left',
  },
})

export type TableProps<RootComponent extends ElementType = 'table', TableElement extends HTMLElement = HTMLTableElement> = {
  /** 自訂根元素標籤 */
  'component'?: RootComponent
  /** 子內容（通常為 TableHead / TableBody） */
  'children'?: ReactNode
  /**
   * 儲存格內距模式，會透過 context 傳遞給所有 TableCell
   * @default 'normal'
   */
  'padding'?: TableCellPadding
  /**
   * 儲存格大小，會透過 context 傳遞給所有 TableCell
   * @default 'medium'
   */
  'size'?: TableCellSize
  /**
   * 是否啟用固定表頭
   * @default false
   */
  'stickyHeader'?: boolean
  /** 自訂根元素的 StyleX 樣式 */
  'rootStyle'?: stylex.StyleXStyles
  /** 根元素的 ref */
  'rootRef'?: ForwardedRef<TableElement>
  /** 測試用 data 屬性 */
  'data-testid'?: string
} & ExtendStyleProps & ComponentPropsWithoutRef<RootComponent>

const TableComponent = <RootComponent extends ElementType = 'table', TableElement extends HTMLElement = HTMLTableElement>(
  props: TableProps<RootComponent, TableElement>,
) => {
  const {
    component = 'table',
    padding = 'normal',
    size = 'medium',
    stickyHeader = false,
    extendStyles,
    rootStyle,
    rootRef,
    children,
    ...rest
  } = makeExtendStyle(props)

  const Component = component as ElementType

  const tableContextValue: TableContextValue = useMemo(
    () => ({
      padding,
      size,
      stickyHeader,
    }),
    [padding, size, stickyHeader],
  )

  return (
    <TableContext.Provider value={tableContextValue}>
      <Component
        ref={rootRef}
        role={component === 'table' ? undefined : 'table'}
        {...stylex.props(
          _baseStyles.root,
          stickyHeader && _baseStyles.stickyHeader,
          rootStyle,
          extendStyles,
        )}
        {...rest}
      >
        {children}
      </Component>
    </TableContext.Provider>
  )
}

/**
 * Table 是表格根元件，提供 TableContext 給子元件共享 padding、size、stickyHeader 設定。
 */
export const Table = memo(TableComponent, isEqual) as typeof TableComponent
